import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import { Add, Delete, Edit, Help } from "@mui/icons-material";
import { useSelector } from "react-redux";
import {
  HtmlTooltip,
  HtmlTooltipDisabled,
  StyledDarkRedInformationTypography,
} from "../../../components/commonComponents/StyledComponents/styled";
import ActionButton from "../../../components/commonComponents/Buttons/ActionButton";
import { DisabledMutedGreyButton } from "../../../components/commonComponents/Buttons/DisabledButton";
import { ListingCellCenter, ListingCellLeft, ListingCellRight, ListingHeaderContent } from "../../../components/commonComponents/TabularLists/styles";
import AddUser from "../modalContent/AddUser";
import { SortIcon } from "../../../api_calls/utils";
import {
  checkUserDetailsFromCookie,
  checkUserPermissions,
  deleteUser,
  deleteUserFromSpecificCompany,
  manageUsersPaginationCookie,
  normalizeUserListType,
  resetUserData,
  userListFilterCookieKey,
  userListStateKey,
} from "../../../api_calls/userManagement";
import {
  getBottomNavRetailerSelectionFromCookies,
  isLockedOutsideAllRetailers,
  resolveRetailerSelectionOptions,
} from "../../../api_calls/retailerSelection";
import Confirmation from "../../../components/commonComponents/Confirmation";
import { toast } from "react-toastify";
import { CustomTextInput } from "../../../components/commonComponents/TextInputs/style";
import Pagination from "../../../components/commonComponents/LoadMore/Pagination";
import { createCookie, deleteCookie } from "../../../api_calls/requestConfig";
import ListingDataTable from "../../../components/commonComponents/TabularLists/ListingDataTable";
import ExpandableNameList from "../../../components/commonComponents/TabularLists/ExpandableNameList";
import RetailerIconCount from "../../../components/commonComponents/TabularLists/RetailerIconCount";
import RolesByCompanyIcon from "../../../components/commonComponents/TabularLists/RolesByCompanyIcon";
import SingleSelect from "../../../components/commonComponents/Selectors/SingleSelect";
import { tenantRetailerScopeAllowsOperation } from "../../../api_calls/tenantRetailerRecordAccess";

// Tooltip shown on New / Edit / Delete when a specific retailer (not All Retailers) is selected
const ACCOUNT_MANAGEMENT_ALL_RETAILERS_TOOLTIP = "Only in All Retailers";

const UserList = ({ usedFor }) => {
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [modalDetails, setModalDetails] = React.useState({});
  const [showConfirmation, setShowConfirmation] = React.useState(false);
  const [userToDelete, setUserToDelete] = React.useState({});
  // multi-company delete: pick which workspace to remove the user from
  const [showMultiCompanyConfirmation, setShowMultiCompanyConfirmation] = React.useState(false);
  const [userToDeleteFromCompany, setUserToDeleteFromCompany] = React.useState({});
  const [fieldError, setFieldError] = React.useState({});
  const [searchVal, setSearchVal] = React.useState({ value: "" });
  const allowedAccessRights = checkUserPermissions();
  const permissionScope = allowedAccessRights.allowed_access;

  const checkUserDetails = checkUserDetailsFromCookie();
  const loggedInUserType = checkUserDetails.user_detail?.user_type;
  const companyAllowedUserTypes = ["cyreen", "branch", "tenant", "agency"];
  // Retailer roots are only meaningful to viewers who can span more than one retailer
  const showRetailerColumn = ["tenant", "cyreen"].includes(checkUserDetails.user_detail.user_type);
  const normalizedUserType = normalizeUserListType(usedFor);
  // Company stays on the Users (retailer) tab. Cyreen uses Retailer only.
  const hideCompanyColumn = normalizedUserType === "cyreen";
  // Cyreen / Agency lists use the icon+count Retailer cell; customer Users stay list-based.
  const useRetailerIconCount = ["cyreen", "agency"].includes(normalizedUserType);
  const stateKey = userListStateKey(normalizedUserType);
  const filterCookieKeyword = userListFilterCookieKey(normalizedUserType);

  const currentStateData = useSelector((state) => state.userSelections);
  const isCustomerUsersTab = normalizedUserType === "retailer";

  // Prefer Redux bottom-nav selection so the lock stays in sync; fall back to cookie
  const bottomNavRetailerSelection =
    typeof currentStateData.bottom_nav_selected_retailer !== "undefined"
      ? currentStateData.bottom_nav_selected_retailer
      : getBottomNavRetailerSelectionFromCookies();
  // Lock only if the user can switch retailers and did not pick All Retailers.
  // One company (static name, no dropdown) stays unlocked.
  const isAccountManagementLocked = isLockedOutsideAllRetailers(
    bottomNavRetailerSelection,
    resolveRetailerSelectionOptions(currentStateData)
  );

  // sorting measures for files listing
  const [sortingMeasure, setSortingMeasure] = React.useState({
    username: "asc",
    email: "asc",
  });

  const [sortingParamName, setSortingParamName] = React.useState("username");

  const paginationStartValue =
    typeof currentStateData.users_pagination !== "undefined" ? currentStateData.users_pagination.user_list_start : 0;
  const paginationEndValue =
    typeof currentStateData.users_pagination !== "undefined" ? currentStateData.users_pagination.user_list_end : 10;

  const userRecords =
    typeof currentStateData[stateKey] !== "undefined" && currentStateData[stateKey].length !== 0
      ? currentStateData[stateKey]
      : [];

  // to handle loading state for the data table listing.
  const userRecordsLoading = typeof currentStateData[stateKey] === "undefined" && userRecords.length === 0
    ? true
    : false;

  const applyFilter = () => {
    if (userRecords.length !== 0) {
      createCookie(filterCookieKeyword, JSON.stringify(searchVal.value));

      return sortedResults(
        userRecords.filter((user) => {
          const username = typeof user.username !== "undefined" && user.username !== null ? user.username : "";
          const email = typeof user.email !== "undefined" && user.email !== null ? user.email : "";
          const roles = Array.isArray(user.roles) ? user.roles : [];
          const assignedCompanyName = typeof user.assigned_company !== "undefined" && typeof user.assigned_company.name !== "undefined"
            ? user.assigned_company.name
            : "";
          const assignedCompanyNames = (Array.isArray(user.assigned_companies) ? user.assigned_companies : [])
            .map((company) => company.name)
            .join(", ");
          // retailer roots shown in the Retailer column are searchable too
          const retailerNames = (Array.isArray(user.retailers) ? user.retailers : [])
            .map((retailer) => retailer.name)
            .join(", ");
          return (
            username.toLowerCase().includes(searchVal.value.toLowerCase().toString()) ||
            email.toLowerCase().toString().includes(searchVal.value.toLowerCase().toString()) ||
            assignedCompanyName.toLowerCase().includes(searchVal.value.toLowerCase().toString()) ||
            assignedCompanyNames.toLowerCase().includes(searchVal.value.toLowerCase().toString()) ||
            retailerNames.toLowerCase().includes(searchVal.value.toLowerCase().toString()) ||
            roles.join(",").toLowerCase().includes(searchVal.value.toLowerCase().toString())
          );
        })
      );
    } else {
      return [];
    }
  };

  // to return the sorted list items
  const sortedResults = (toBeSortedList) => {
    return toBeSortedList.slice().sort((a, b) => {
      const firstUsername = typeof a.username !== "undefined" && a.username !== null ? a.username : "";
      const secondUsername = typeof b.username !== "undefined" && b.username !== null ? b.username : "";
      const firstEmail = typeof a.email !== "undefined" && a.email !== null ? a.email : "";
      const secondEmail = typeof b.email !== "undefined" && b.email !== null ? b.email : "";

      if (sortingParamName === "username") {
        return sortingMeasure.username === "asc"
          ? firstUsername.localeCompare(secondUsername)
          : secondUsername.localeCompare(firstUsername);
      } else if (sortingParamName === "email") {
        return sortingMeasure.email === "asc" ? firstEmail.localeCompare(secondEmail) : secondEmail.localeCompare(firstEmail);
      } else {
        return sortingMeasure.username === "asc"
          ? firstUsername.localeCompare(secondUsername)
          : secondUsername.localeCompare(firstUsername);
      }
    });
  };

  let listItems = typeof searchVal.value !== "undefined" && searchVal.value !== "" ? applyFilter() : userRecords;

  let userListitems =
    listItems.length !== 0 ? sortedResults(listItems).slice(paginationStartValue, paginationEndValue) : [];

  const closeModal = () => {
    setModalDetails({});
    setIsModalOpen(false);
    resetUserData();
  };

  const openModal = (modalFor, userData) => {
    setModalDetails({
      openFor: modalFor,
      user_data: userData,
    });
    setIsModalOpen(true);
  };

  const editAction = (user) => {
    return (
      <HtmlTooltip title={<Typography variant="body4">Edit</Typography>}>
        <a
          href="javacript:void(0)"
          aria-label={"edit user for " + user.username}
          onClick={(e) => {
            e.preventDefault();
            openModal("update", user);
          }}
        >
          <Edit sx={{ color: "#AF3241" }} />
        </a>
      </HtmlTooltip>
    );
  };

  const deleteAction = (user) => {
    return (
      <HtmlTooltip title={<Typography variant="body4">Delete</Typography>}>
        <a
          href="javacript:void(0)"
          aria-label={"delete user for " + user.username}
          onClick={(e) => {
            e.preventDefault();
            // Pick a company when the user belongs to more than one
            if (user.multiple_accounts === true) {
              setShowMultiCompanyConfirmation(true);
            } else {
              setShowConfirmation(true);
            }
            setUserToDelete(user);
          }}
        >
          <Delete sx={{ color: "#AF3241" }} />
        </a>
      </HtmlTooltip>
    );
  };

  // tooltipTitle defaults to "No Access"; retailer lock passes ACCOUNT_MANAGEMENT_ALL_RETAILERS_TOOLTIP
  const disabledEditAction = (user, tooltipTitle = "No Access") => {
    return (
      <HtmlTooltipDisabled title={tooltipTitle}>
        <a
          href="javacript:void(0)"
          onClick={(e) => {
            e.preventDefault();
          }}
        >
          <Edit sx={{ color: "#E6E6E6" }} />
        </a>
      </HtmlTooltipDisabled>
    );
  };

  const disabledDeleteAction = (user, tooltipTitle = "No Access") => {
    return (
      <HtmlTooltipDisabled title={tooltipTitle}>
        <a
          href="javacript:void(0)"
          onClick={(e) => {
            e.preventDefault();
          }}
        >
          <Delete sx={{ color: "#E6E6E6" }} />
        </a>
      </HtmlTooltipDisabled>
    );
  };

  const handleSortingParamChange = (paramName) => {
    setSortingMeasure((prevState) => {
      return {
        ...prevState,
        [paramName]: prevState[paramName] === "asc" ? "desc" : "asc",
      };
    });
    setSortingParamName(paramName);
  };

  // user.retailers is the Retailer column. A tenant may edit or delete only when every name is assigned to them.
  const tenantMayOperateOnUser = (user) =>
    tenantRetailerScopeAllowsOperation(
      loggedInUserType,
      (Array.isArray(user.retailers) ? user.retailers : []).map((retailer) => retailer.name),
      currentStateData
    );

  const columns = React.useMemo(() => {
    let cols = [
      {
        id: "username",
        header: (
          <ListingHeaderContent onClick={() => handleSortingParamChange("username")} style={{ cursor: "pointer" }}>
            Username <SortIcon direction={sortingMeasure.username} />
          </ListingHeaderContent>
        ),
        render: (user) => (
          <ListingCellLeft>
            {user.username}
          </ListingCellLeft>
        ),
      },
      {
        id: "email",
        header: (
          <ListingHeaderContent onClick={() => handleSortingParamChange("email")} style={{ cursor: "pointer" }}>
            E-mail <SortIcon direction={sortingMeasure.email} />
          </ListingHeaderContent>
        ),
        render: (user) => (
          <ListingCellLeft>
            {user.email}
          </ListingCellLeft>
        ),
      }
    ];

    // Shared order: Username, E-mail, Company, Retailer, Role, Action.
    // A list simply skips a column when that list does not show it.
    if (companyAllowedUserTypes.includes(checkUserDetails.user_detail.user_type) && hideCompanyColumn !== true) {
      cols.push({
        id: "company",
        cellClassName: "wrapLargeText",
        cellSx: { maxWidth: 180 },
        header: (
          <ListingHeaderContent>
            Company
          </ListingHeaderContent>
        ),
        render: (user) => (
          <ListingCellLeft>
            <ExpandableNameList
              maxVisible={isCustomerUsersTab === true ? 3 : 2}
              items={(Array.isArray(user.assigned_companies) ? user.assigned_companies : []).map((company, index) => {
                return {
                  key: company.id + "_" + company.name + "_" + index,
                  label: company.name,
                };
              })}
            />
          </ListingCellLeft>
        ),
      });
    }

    if (showRetailerColumn === true) {
      cols.push({
        id: "retailer",
        cellClassName: useRetailerIconCount === true ? undefined : "wrapLargeText",
        cellSx: useRetailerIconCount === true ? undefined : { maxWidth: 180 },
        headerAlign: useRetailerIconCount === true ? "center" : undefined,
        align: useRetailerIconCount === true ? "center" : undefined,
        header: (
          <ListingHeaderContent>
            Retailer
          </ListingHeaderContent>
        ),
        render: (user) => {
          // Backend already scopes companies/retailers; show the list as returned
          const retailers = Array.isArray(user.retailers) ? user.retailers : [];

          // Cyreen / Agency: icon + count. Customer Users: comma list, extra names in a tooltip.
          if (useRetailerIconCount === true) {
            return (
              <ListingCellCenter>
                <RetailerIconCount names={retailers.map((retailer) => retailer.name)} />
              </ListingCellCenter>
            );
          }

          return (
            <ListingCellLeft>
              <ExpandableNameList
                maxVisible={3}
                items={retailers.map((retailer, index) => {
                  return {
                    key: retailer.id + "_" + index,
                    label: retailer.name,
                  };
                })}
              />
            </ListingCellLeft>
          );
        },
      });
    }

    cols = [
      ...cols,
      {
        id: "roles",
        headerAlign: "center",
        align: "center",
        header: (
          <ListingHeaderContent>
            Role
          </ListingHeaderContent>
        ),
        render: (user) => (
          <ListingCellCenter>
            <RolesByCompanyIcon
              simplifyTooltipWhenUniform={["cyreen", "retailer"].includes(normalizedUserType)}
              companies={Array.isArray(user.assigned_companies) ? user.assigned_companies : []}
            />
          </ListingCellCenter>
        ),
      },
      {
        id: "actions",
        headerAlign: "right",
        align: "right",
        header: (
          <ListingHeaderContent>
            Action
          </ListingHeaderContent>
        ),
        render: (user) => (
          <ListingCellRight>
            {/* Specific retailer selected → always disable with All Retailers tooltip */}
            {isAccountManagementLocked === true ? (
              <>
                {disabledEditAction(user, ACCOUNT_MANAGEMENT_ALL_RETAILERS_TOOLTIP)}
                {disabledDeleteAction(user, ACCOUNT_MANAGEMENT_ALL_RETAILERS_TOOLTIP)}
              </>
            ) : normalizedUserType === "cyreen" ? (
              <>
                {/* ----- edit ---- */}
                {permissionScope.user_list_can_edit_cyreen === true && tenantMayOperateOnUser(user) === true
                  ? editAction(user)
                  : disabledEditAction(user)}
                {/* ----- delete ----- */}
                {permissionScope.user_list_can_delete_cyreen === true && tenantMayOperateOnUser(user) === true
                  ? deleteAction(user)
                  : disabledDeleteAction(user)}
              </>
            ) : (
              <>
                {typeof user.user_type !== "undefined" && user.user_type.toLowerCase() === "brand" ? (
                  <>
                    {/* ----- edit ---- */}
                    {permissionScope.user_list_can_edit_brand === true && tenantMayOperateOnUser(user) === true
                      ? editAction(user)
                      : disabledEditAction(user)}
                    {/* ----- delete ----- */}
                    {permissionScope.user_list_can_delete_brand === true && tenantMayOperateOnUser(user) === true
                      ? deleteAction(user)
                      : disabledDeleteAction(user)}
                  </>
                ) : (
                  <>
                    {/* ----- edit ---- */}
                    {permissionScope.user_list_can_edit_external === true && tenantMayOperateOnUser(user) === true
                      ? editAction(user)
                      : disabledEditAction(user)}
                    {/* ----- delete ----- */}
                    {permissionScope.user_list_can_delete_external === true && tenantMayOperateOnUser(user) === true
                      ? deleteAction(user)
                      : disabledDeleteAction(user)}
                  </>
                )}
              </>
            )}
          </ListingCellRight>
        ),
      }
    ]

    return cols;
  }, [sortingMeasure, normalizedUserType, isAccountManagementLocked, showRetailerColumn, hideCompanyColumn, useRetailerIconCount, isCustomerUsersTab, loggedInUserType, currentStateData]);

  React.useEffect(() => {
    // check if filter cookie is set and set it in redux state
    if (typeof document.cookie.split("; ").find((row) => row.startsWith(filterCookieKeyword)) !== "undefined") {
      const filterObj = JSON.parse(
        document.cookie
          .split("; ")
          .find((row) => row.startsWith(filterCookieKeyword))
          .split("=")[1]
      );
      setSearchVal({ value: filterObj });
    }

    // reset pagination redux state
    manageUsersPaginationCookie({
      user_list_start: 0,
      user_list_end: 10,
    });

  }, [filterCookieKeyword, usedFor]);

  return (
    <>
      {/* Page-level notice when account management is locked to All Retailers view */}
      {isAccountManagementLocked === true && (
        <>
          <StyledDarkRedInformationTypography variant="body2">
            Accounts with access to the currently selected retailer are displayed for reference only. Account
            management is only available in the “All Retailers” view.
          </StyledDarkRedInformationTypography>
          <Box height={20} />
        </>
      )}

      {/* ============ Action buttons =========== */}
      <Stack direction="row" spacing={2}>
        {normalizedUserType !== "cyreen" &&
          (permissionScope.user_list_can_edit_brand === true || permissionScope.user_list_can_edit_external === true) ? (
          isAccountManagementLocked === true ? (
            // Muted grey stand-in so the locked action keeps the same footprint as the live button
            <DisabledMutedGreyButton
              onClick={(e) => {
                e.preventDefault();
              }}
              label="New User"
              icon={<Add />}
              toolTipContent={ACCOUNT_MANAGEMENT_ALL_RETAILERS_TOOLTIP}
              sx={{ mt: 3, mb: 2 }}
            />
          ) : (
            <ActionButton
              onClick={(e) => {
                e.preventDefault();
                openModal("add", {});
              }}
              label="New User"
              icon={<Add />}
            />
          )
        ) : normalizedUserType === "cyreen" && permissionScope.user_list_can_edit_cyreen === true ? (
          isAccountManagementLocked === true ? (
            <DisabledMutedGreyButton
              onClick={(e) => {
                e.preventDefault();
              }}
              label={"New Cyreen User"}
              icon={<Add />}
              toolTipContent={ACCOUNT_MANAGEMENT_ALL_RETAILERS_TOOLTIP}
              sx={{ mt: 3, mb: 2 }}
            />
          ) : (
            <ActionButton
              onClick={(e) => {
                e.preventDefault();
                openModal("add", {});
              }}
              label={"New Cyreen User"}
              icon={<Add />}
            />
          )
        ) : (
          <></>
        )}
      </Stack>
      <Box height={30} />
      <Box width={350}>
        <CustomTextInput
          fullWidth
          variant="standard"
          placeholder="Search"
          label="Search"
          autoComplete="off"
          type={"text"}
          value={searchVal.value}
          onChange={(e) => {
            const nextSearchValue = e.target.value;
            setSearchVal({ value: nextSearchValue });
            if (nextSearchValue === "") {
              deleteCookie(filterCookieKeyword, true);
            }
            // searching changes the filtered set — always show results from page 1
            const pageSize =
              paginationEndValue > paginationStartValue
                ? paginationEndValue - paginationStartValue
                : 10;
            manageUsersPaginationCookie({
              user_list_start: 0,
              user_list_end: pageSize,
            });
          }}
          onBlur={(e) => { }}
        />
      </Box>

      {/* ============== users listing ============ */}
      <Box height={30} />
      <ListingDataTable
        columns={columns}
        rows={userListitems}
        rowKey={(user) => `${user.id}-${user.workspace_id}`}
        emptyText="No users found"
        loading={userRecordsLoading}
      />
      {/* --------- record count info and pagination --------- */}
      <Stack direction="row" spacing={2} justifyContent="center" alignItems="center" mt={2} mb={5}>
        <Pagination
          totalCount={listItems.length}
          reduxPaginationKeyword="users_pagination"
          reduxStartKeyword="user_list_start"
          reduxEndKeyword="user_list_end"
          useWindowScroll={true}
        />
      </Stack>


      {/* ============ modal popup for add/update User ========== */}
      {isModalOpen && (
        <AddUser
          open={isModalOpen}
          onClose={closeModal}
          size={"md"}
          closeModal={closeModal}
          usedFor={modalDetails.openFor}
          userInfo={modalDetails.user_data}
          cyreenBoolean={normalizedUserType === "cyreen" ? true : false}
          refreshUserType={normalizedUserType}
        />
      )}
      {/* ---------- delete Confirmation ---------- */}
      {showConfirmation === true && (
        <Confirmation
          confirmationActionCall={() => {
            deleteUser(userToDelete, normalizedUserType).then((info) => {
              if (info.success === true) {
                setUserToDelete({});
                setShowConfirmation(false);
                toast("User deleted successfully ", { type: "success" });
              } else {
                toast("Sorry, something went wrong", { type: "error" });
              }
            });
          }}
          closePopup={(e) => {
            setUserToDelete({});
            setShowConfirmation(false);
          }}
          showConfirmationPopup={showConfirmation}
          confirmationTitle={"Delete User: " + userToDelete.username}
          confirmationMessage={
            <>
              <Typography variant="body2">
                You are deleting a user account. This action is permanent and cannot be reversed.
              </Typography>
              <Box height={20} />
              <Typography variant="body2">Are you sure you want to proceed?</Typography>
            </>
          }
        />
      )}
      {/* ------------ delete confirmation when the user belongs to multiple companies ------ */}
      {showMultiCompanyConfirmation === true && (
        <Confirmation
          confirmationActionCall={() => {
            setFieldError({});
            if (typeof userToDeleteFromCompany.id === "undefined") {
              setFieldError({ ...fieldError, user_company: "Please select a company" });
              return;
            } else {
              deleteUserFromSpecificCompany(userToDelete, userToDeleteFromCompany.id, normalizedUserType).then((info) => {
                if (info.success === true) {
                  setUserToDelete({});
                  setShowMultiCompanyConfirmation(false);
                  setUserToDeleteFromCompany({});
                  setFieldError({});
                  toast("User deleted successfully", { type: "success" });
                } else {
                  toast("Sorry, something went wrong", { type: "error" });
                }
              });
            }
          }}
          closePopupOnClose={false}
          closePopup={(e) => {
            setUserToDelete({});
            setShowMultiCompanyConfirmation(false);
            setUserToDeleteFromCompany({});
            setFieldError({});
          }}
          showConfirmationPopup={showMultiCompanyConfirmation}
          confirmationTitle={"Delete User: " + userToDelete.username}
          confirmationMessage={
            <>
              <SingleSelect
                enableFullWidth={true}
                label={
                  <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    Company Selection*{" "}
                    <HtmlTooltip
                      title={
                        <Box width={200}>
                          <Typography variant="body2">
                            This user belongs to more than one company. Select the company from which you want to
                            remove this account.
                          </Typography>
                        </Box>
                      }
                    >
                      <Help fontSize="small" />
                    </HtmlTooltip>
                  </span>
                }
                selectionCheck={(option, value) => option.label === value.label}
                sortOptions={false}
                options={
                  typeof userToDelete.assigned_companies !== "undefined"
                    ? userToDelete.assigned_companies.map((companyDtl) => {
                        return { id: companyDtl.id, label: companyDtl.name };
                      })
                    : []
                }
                defaultVal={userToDeleteFromCompany}
                onChangeCall={(selectedOption) => {
                  setUserToDeleteFromCompany(selectedOption);
                }}
                fieldError={
                  typeof fieldError.user_company !== "undefined" && fieldError.user_company !== ""
                    ? fieldError.user_company
                    : ""
                }
              />
              <Box height={20} />
              <Typography variant="body2">
                You are deleting a user account from the selected company. This action is permanent and cannot be
                reversed.
              </Typography>
              <Box height={20} />
              <Typography variant="body2">Are you sure you want to proceed?</Typography>
            </>
          }
        />
      )}
    </>
  );
};

export default UserList;
